import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { AnimatePresence, MotionConfig } from "framer-motion";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { SecurityHeaders } from "@/components/layout/SecurityHeaders";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { Loader2 } from "lucide-react";
import { isBot } from "@/lib/isBot";

// iOS detection for disabling Framer Motion transforms
const isIOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

function getIsPortrait() {
  if (typeof window === "undefined") return true;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(orientation: portrait)").matches;
  }
  return window.innerHeight >= window.innerWidth;
}

// Eager load critical pages for best initial paint
import Index from "./pages/Index";
import Products from "./pages/Products";

// Lazy load non-critical pages to reduce initial bundle size
const About = lazy(() => import("./pages/About"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Contact = lazy(() => import("./pages/Contact"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Lazy load admin pages (rarely accessed by most users)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminShipping = lazy(() => import("./pages/admin/Shipping"));
const AdminGiftCards = lazy(() => import("./pages/admin/GiftCards"));
const AdminBlog = lazy(() => import("./pages/admin/Blog"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminPhysicalCards = lazy(() => import("./pages/admin/PhysicalCards"));
const AdminDiscountCodes = lazy(() => import("./pages/admin/DiscountCodes"));

const queryClient = new QueryClient();

// Minimal loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

const App = () => {
  // Skip loading screen entirely for bots/crawlers to ensure content is indexed
  // CRITICAL FIX: Also skip loading screen on iOS to prevent zoom bug
  const [isLoading, setIsLoading] = useState(() => {
    if (isBot()) return false;
    // FIXED: Skip loading screen on iOS to prevent animation-triggered zoom
    if (isIOS) return false;
    return true;
  });

  // Calculate actual viewport height for mobile browsers (fixes Android gesture nav)
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);

    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, []);

  // Apply bot class to HTML element for CSS-based animation overrides
  useEffect(() => {
    if (isBot()) {
      document.documentElement.classList.add("is-bot");
    }
  }, []);

  useEffect(() => {
    // Skip loading timer for bots and iOS
    if (isBot() || isIOS) return;

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <MotionConfigWrapper>
            <AuthProvider>
              <CartProvider>
                <SecurityHeaders />
                <ScrollToTop />
                <Toaster />
                <Sonner />
                <CartDrawer />
                {/* CRITICAL FIX: Only show AnimatePresence if not iOS */}
                {!isIOS && (
                  <AnimatePresence mode="wait">{isLoading && <LoadingScreen key="loading" />}</AnimatePresence>
                )}
                {/* FIXED: Simple fade for iOS (no AnimatePresence) */}
                {isIOS && isLoading && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 9999,
                      opacity: isLoading ? 1 : 0,
                      transition: "opacity 0.3s ease-out",
                      pointerEvents: isLoading ? "auto" : "none",
                    }}
                  >
                    <LoadingScreen />
                  </div>
                )}
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/testimonials" element={<Testimonials />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/order-success" element={<OrderSuccess />} />
                    <Route path="/gift-cards" element={<GiftCards />} />
                    <Route path="/track-order" element={<TrackOrder />} />
                    <Route path="/new-arrivals" element={<Products />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />

                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="shipping" element={<AdminShipping />} />
                      <Route path="gift-cards" element={<AdminGiftCards />} />
                      <Route path="blog" element={<AdminBlog />} />
                      <Route path="analytics" element={<AdminAnalytics />} />
                      <Route path="physical-cards" element={<AdminPhysicalCards />} />
                      <Route path="discount-codes" element={<AdminDiscountCodes />} />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </CartProvider>
            </AuthProvider>
          </MotionConfigWrapper>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

/**
 * MotionConfigWrapper - Disables all Framer Motion animations on iOS /products in portrait mode
 * This prevents transforms from being applied that cause Safari's compositor zoom bug
 */
function MotionConfigWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // CRITICAL FIX: Always disable motion on iOS
  const [reducedMotion, setReducedMotion] = useState<"always" | "never">(() => {
    if (typeof window === "undefined") return "never";
    // FIXED: Disable ALL motion on iOS, not just products page
    if (isIOS) return "always";
    const isProductsPage = location.pathname === "/products";
    const isPortrait = getIsPortrait();
    return isProductsPage && isPortrait ? "always" : "never";
  });

  useEffect(() => {
    const checkMotion = () => {
      // FIXED: Always reduce motion on iOS
      if (isIOS) {
        setReducedMotion("always");
        return;
      }

      const isProductsPage = location.pathname === "/products";
      const isPortrait = getIsPortrait();
      const shouldReduce = isProductsPage && isPortrait;
      setReducedMotion(shouldReduce ? "always" : "never");
    };

    // Re-check on mount in case pathname changed during navigation
    checkMotion();

    // Listen for orientation changes
    window.addEventListener("resize", checkMotion);
    window.addEventListener("orientationchange", checkMotion);

    // Also listen for class changes (from early bootstrapper or SecurityHeaders)
    const observer = new MutationObserver(checkMotion);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      window.removeEventListener("resize", checkMotion);
      window.removeEventListener("orientationchange", checkMotion);
      observer.disconnect();
    };
  }, [location.pathname]);

  return <MotionConfig reducedMotion={reducedMotion}>{children}</MotionConfig>;
}

export default App;
